import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, Truck, Cpu, LineChart, Users, ShieldCheck, ArrowRight, CheckCircle2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BENEFITS = [
  { icon: Truck, title: "Fleet-scale claims", desc: "Handle 100 to 10,000 vehicles from one control tower. Bulk file, bulk track, bulk settle." },
  { icon: Cpu, title: "AI Claim API", desc: "Embed our OCR + damage estimator in your app. First 100 calls/month free." },
  { icon: LineChart, title: "Live cost analytics", desc: "Claim ratio, cycle time, cost-per-claim, insurer performance — all in real time." },
  { icon: Users, title: "Dedicated CSM", desc: "Every enterprise gets a named account manager and 24×7 dispatch hotline." },
  { icon: ShieldCheck, title: "SOC 2 & DPDP-ready", desc: "Data-residency in India. Full audit logs. Role-based access controls." },
  { icon: Building2, title: "Custom branding", desc: "White-label the claim journey with your logo and colours. Ship to your users." },
];

const API_SAMPLE = `curl -X POST https://api.myinsuranceclaim.in/v1/claims \\
  -H "Authorization: Bearer $MICLAIM_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "vehicle": { "reg_no": "MH12AB1234", "make": "Maruti" },
    "insurer": "acko",
    "accident_at": "2026-02-14T09:20:00Z",
    "photos": ["https://cdn/photo1.jpg"]
  }'
# → { "claim_id": "MI29841", "ai_estimate_inr": 142300, "cycle_days": 12 }`;

export default function Enterprise() {
  const [form, setForm] = useState({ name: "", company: "", email: "", fleet_size: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email) {
      toast.error("Please fill name, company, and email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/enterprise", form);
      setSubmitted(true);
      toast.success("Demo requested. Our team will reach out within 1 business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(API_SAMPLE);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div data-testid="enterprise-page" className="bg-white dark:bg-slate-950">
      {/* HERO */}
      <section className="relative overflow-hidden mesh-hero py-24">
        <div className="aurora animate-aurora" style={{ width: 500, height: 500, top: -160, right: -100, background: "radial-gradient(circle, #4F46E5 0%, transparent 60%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-semibold text-slate-700 dark:text-slate-200 mb-5">
              <Building2 className="w-3.5 h-3.5 text-blue-800" /> For Enterprises · Fleets · Corporate
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] text-slate-900 dark:text-white">
              Claim <span className="gradient-text">infrastructure</span> for<br />
              India&apos;s biggest fleets.
            </h1>
            <p className="mt-5 text-lg text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed">
              Whether you run 100 corporate cars or 10,000 rideshare vehicles, miclaim gives you
              one dashboard, one API, one SLA — and a claim team that answers when you call.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#demo" className="btn-signature rounded-xl px-6 py-3.5 font-semibold inline-flex items-center gap-2" data-testid="enterprise-hero-demo">
                Book a demo <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#api" className="btn-ghost-outline rounded-xl px-6 py-3.5 font-semibold inline-flex items-center gap-2" data-testid="enterprise-hero-api">
                View API docs
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800 dark:text-blue-400 mb-2">What you get</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              An operating system, not a plugin.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-premium p-6"
                data-testid={`enterprise-benefit-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-700 to-orange-500 flex items-center justify-center mb-4 shadow-lg">
                  <b.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">{b.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* API DOCS TEASER */}
      <section id="api" className="py-20 mesh-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 dark:text-orange-400 mb-2">Claim API</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-4">
              File a claim in <span className="gradient-text-magenta">3 lines of code</span>.
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
              REST API with SDKs for Node, Python, and Go. Webhooks for status updates.
              Sandbox mode with test claims. First 100 API calls/month are free forever.
            </p>
            <ul className="space-y-3">
              {["Real-time AI damage estimate on upload", "Webhook events at every claim stage", "Bulk claim import for fleet migrations", "99.9% uptime SLA · SOC 2 compliant"].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden bg-slate-950 border border-slate-800 shadow-2xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900/50">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-rose-500/70" />
                  <span className="w-3 h-3 rounded-full bg-amber-500/70" />
                  <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
                </div>
                <span className="font-mono text-xs text-slate-500">terminal</span>
                <button onClick={copyCode} className="text-slate-400 hover:text-white transition-colors" data-testid="copy-api-code">
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <pre className="text-xs text-slate-200 font-mono p-5 overflow-x-auto leading-relaxed">
{API_SAMPLE}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* BOOK A DEMO FORM */}
      <section id="demo" className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800 dark:text-blue-400 mb-2">Book a demo</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              See it live in 20 minutes.
            </h2>
            <p className="mt-3 text-slate-600 dark:text-slate-400">Our enterprise team will walk you through the platform, API, and pricing tailored to your fleet.</p>
          </div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-premium p-10 text-center"
              data-testid="enterprise-form-success"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white mb-2">Request received</h3>
              <p className="text-slate-600 dark:text-slate-400">
                Thanks {form.name || "there"}! Our team will reach out to <span className="font-mono">{form.email}</span> within 1 business day.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="card-premium p-8 grid sm:grid-cols-2 gap-4" data-testid="enterprise-form">
              <Input label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="enterprise-name" required />
              <Input label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="enterprise-company" required />
              <Input label="Work email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="enterprise-email" required />
              <Input label="Fleet size" placeholder="e.g. 250 vehicles" value={form.fleet_size} onChange={(v) => setForm({ ...form, fleet_size: v })} testid="enterprise-fleet-size" />
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">What are you trying to solve?</label>
                <textarea
                  className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="enterprise-message"
                  placeholder="Tell us about your fleet, current pain points, and timeline..."
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-signature rounded-xl px-6 py-3.5 font-semibold w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="enterprise-submit"
                >
                  {submitting ? "Sending..." : (<>Request demo <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, required, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">{label}{required && <span className="text-rose-500">*</span>}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent"
        data-testid={testid}
      />
    </div>
  );
}
